/**
 * One report screen for the owner and for a public share link.
 * A short brief, ten actions, then the rest folded by when to do them.
 */

import { useEffect, useMemo, useState } from "react";
import { ExecutiveSummary } from "./ExecutiveSummary";
import { ProgressTracker } from "./ProgressTracker";
import { ActionDetailPanel } from "./ActionDetailPanel";
import { CopyInstructionButton } from "./CopyInstructionButton";
import { actionProgressStats } from "../../utils/actionProgress";
import { presentActions, suggestionKinds } from "../../utils/reportPresentation";

function kindLabel(name) {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function KindButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`text-sm ${
        active
          ? "font-medium text-foreground underline decoration-foreground/40 underline-offset-4"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

export function SEODashboardV2({
  actions: externalActions = [],
  executiveSummary,
  onStatusChange: externalOnStatusChange,
  onRefresh,
  domain,
  jobId,
  onSummaryGenerated,
  isShared = false,
  readOnly = false,
  onExportMarkdown = null,
}) {
  const [internalActions, setInternalActions] = useState([]);
  const [statusOverrides, setStatusOverrides] = useState({});
  const [selectedAction, setSelectedAction] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [kind, setKind] = useState(null);

  const sourceActions = externalActions.length > 0 ? externalActions : internalActions;
  const actions = useMemo(
    () =>
      sourceActions.map((action) =>
        statusOverrides[action.id]
          ? { ...action, status: statusOverrides[action.id] }
          : action,
      ),
    [sourceActions, statusOverrides],
  );

  useEffect(() => {
    setStatusOverrides((current) => {
      const next = {};
      let changed = false;
      Object.entries(current).forEach(([id, status]) => {
        const match = sourceActions.find((action) => action.id === id);
        if (match && match.status === status) {
          changed = true;
          return;
        }
        next[id] = status;
      });
      return changed ? next : current;
    });
  }, [sourceActions]);
  const kinds = useMemo(() => suggestionKinds(actions), [actions]);
  const visibleActions = useMemo(
    () => (kind ? actions.filter((action) => action.category === kind) : actions),
    [actions, kind],
  );
  const { lead, groups } = useMemo(
    () => presentActions(visibleActions),
    [visibleActions],
  );
  const stats = useMemo(() => actionProgressStats(actions), [actions]);

  useEffect(() => {
    const openFromUrl = () => {
      const actionId = new URLSearchParams(window.location.search).get("actionId");
      if (!actionId) {
        setSelectedAction(null);
        return;
      }
      const match = actions.find((action) => action.id === actionId);
      if (match) setSelectedAction(match);
    };

    openFromUrl();
    window.addEventListener("popstate", openFromUrl);
    return () => window.removeEventListener("popstate", openFromUrl);
  }, [actions]);

  const handleStatusChange = async (id, newStatus) => {
    if (readOnly || isShared) return;
    const previous = actions.find((action) => action.id === id)?.status;
    setStatusOverrides((current) => ({ ...current, [id]: newStatus }));
    setSelectedAction((current) =>
      current?.id === id ? { ...current, status: newStatus } : current,
    );
    if (!externalOnStatusChange) {
      setInternalActions((prev) =>
        prev.map((action) =>
          action.id === id ? { ...action, status: newStatus } : action,
        ),
      );
      return;
    }

    const saved = await externalOnStatusChange(id, newStatus);
    if (saved === false) {
      setStatusOverrides((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
      setSelectedAction((current) =>
        current?.id === id ? { ...current, status: previous } : current,
      );
    }
  };

  const handleActionClick = (action) => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("actionId", action.id);
    window.history.pushState({ actionId: action.id }, "", currentUrl.toString());
    setSelectedAction(action);
  };

  const handleClose = () => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("actionId");
    window.history.pushState({}, "", currentUrl.toString());
    setSelectedAction(null);
  };

  const renderAction = (action) => {
    const done = action.status === "completed";
    return (
      <li
        key={action.id}
        className="flex items-center justify-between gap-4 border-b border-border"
      >
        <button
          type="button"
          onClick={() => handleActionClick(action)}
          className={`min-w-0 flex-1 py-3 text-left text-base ${
            done
              ? "text-muted-foreground/40"
              : "text-foreground hover:text-blue-700"
          }`}
        >
          {done && (
            <span className="mr-2 text-xs font-medium uppercase tracking-wide">
              Done
            </span>
          )}
          <span className={done ? "line-through decoration-current" : undefined}>
            {action.title}
          </span>
        </button>
        <CopyInstructionButton
          action={action}
          site={domain}
          className="shrink-0 py-3 text-sm text-muted-foreground hover:text-foreground"
        />
      </li>
    );
  };

  return (
    <div className="bg-background">
      <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        {domain && !isShared && (
          <h1 className="mb-6 truncate text-3xl font-semibold tracking-tight text-foreground">
            {domain}
          </h1>
        )}
        <div className="mb-6 flex flex-wrap items-center justify-end gap-4">
          {stats.completedActions > 0 && (
            <p className="text-sm text-muted-foreground">
              {stats.completedActions} of {stats.totalActions} done
            </p>
          )}
          {kinds.length > 1 && (
            <button
              type="button"
              onClick={() => setShowFilter((open) => !open)}
              className="text-sm text-muted-foreground hover:text-foreground"
              aria-expanded={showFilter}
            >
              {kind ? `Filter · ${kindLabel(kind)}` : "Filter"}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowProgress((open) => !open)}
            className="text-sm text-muted-foreground hover:text-foreground"
            aria-expanded={showProgress}
          >
            Progress
          </button>
          {onExportMarkdown && !isShared && (
            <button
              type="button"
              onClick={onExportMarkdown}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Export
            </button>
          )}
          {onRefresh && !isShared && !readOnly && (
            <button
              type="button"
              onClick={onRefresh}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Refresh
            </button>
          )}
        </div>

        {showFilter && (
          <div
            className="mb-6 flex flex-wrap gap-x-4 gap-y-2"
            role="group"
            aria-label="Kind of suggestion"
          >
            <KindButton active={!kind} onClick={() => setKind(null)}>
              All
            </KindButton>
            {kinds.map((name) => (
              <KindButton
                key={name}
                active={kind === name}
                onClick={() => setKind(name)}
              >
                {kindLabel(name)}
              </KindButton>
            ))}
          </div>
        )}

        <ExecutiveSummary
          summary={executiveSummary}
          domain={domain}
          jobId={jobId}
          onSummaryGenerated={onSummaryGenerated}
          readOnly={readOnly || isShared}
        />

        {showProgress && (
          <div className="mb-8">
            <ProgressTracker stats={stats} horizontal />
          </div>
        )}

        {lead.length > 0 && (
          <ol className="mb-8">{lead.map(renderAction)}</ol>
        )}

        {groups.map((group) => (
          <details key={group.id} className="border-t border-border py-4">
            <summary className="cursor-pointer text-base font-medium text-foreground">
              {group.label}
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {group.count}
              </span>
            </summary>
            <div className="mt-4 space-y-6">
              {group.categories.map((category) => (
                <div key={category.name}>
                  <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {category.name}
                  </h3>
                  <ul>{category.actions.map(renderAction)}</ul>
                </div>
              ))}
            </div>
          </details>
        ))}

        {actions.length === 0 && (
          <p className="text-sm text-muted-foreground">No actions yet.</p>
        )}
        {actions.length > 0 && lead.length === 0 && groups.length === 0 && (
          <p className="text-sm text-muted-foreground">No actions in this kind.</p>
        )}
      </div>

      {selectedAction && (
        <ActionDetailPanel
          action={selectedAction}
          onClose={handleClose}
          onStatusChange={handleStatusChange}
          readOnly={readOnly || isShared}
          site={domain}
        />
      )}
    </div>
  );
}
